<script>
import { h } from 'vue'

export default {
    name: 'LotFilter',
    data() {
        return {
            loading: true,
            allLots: [],
            displayedLots: [],
            itemsPerPage: 9,
            currentPage: 1,
            searchTerm: '',
            sortField: 'sort', // Default sort by lot number
            sortDirection: 'asc', // Default ascending
            sortDropdownOpen: false, // Track sort dropdown state
            activeFilters: {
                type: [],
                price: [],
                bedrooms: [],
                cars: [],
                floorSize: [],
                sectionSize: [],
                builder: [],
                stage: []
            },
            openDropdown: null, // Track which dropdown is open
            typeOptions: [
                { value: 'sectionEmpty', label: 'Section' },
                { value: 'houseLand', label: 'House & Land' },
                { value: 'showHome', label: 'Show Homes' }
            ],
            preloadedCards: new Map(), // Cache for preloaded card HTML
            loadingCards: new Set(), // Track cards currently being loaded
            viewMode: 'grid', // 'grid' or 'map'
            showMap: true, // Read from data-show-map; when false, hide View buttons and default sections to grid
            mobileFiltersOpen: false, // Track mobile filters popup state
            mobileFilterDropdownOpen: false, // Track mobile filter dropdown state
            windowWidth: window.innerWidth, // Track window width for responsive behavior
            pageEntryType: '',
            /** When "builders", House & Land listing page shows builder catalog instead of packages (see data-hl-catalog-mode on #lot-filter-app). */
            hlCatalogMode: 'lots',
            allBuilders: [],
            displayedBuilders: []
        };
    },
    computed: {
        useBuilderCatalog() {
            return this.hlCatalogMode === 'builders';
        },
        filteredBuilders() {
            let list = this.allBuilders || [];
            if (this.activeFilters.builder.length > 0) {
                list = list.filter(b => this.activeFilters.builder.includes(String(b.id)));
            }
            return [...list].sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
        },
        // Base filtered lots (only type and search filters applied)
        baseFilteredLots() {
            let filtered = this.allLots;

            // Search filter (partial match on title/lot number, builder, heading)
            if (this.searchTerm && this.searchTerm.trim()) {
                filtered = filtered.filter(lot => this.lotMatchesSearch(lot, this.searchTerm));
            }

            // Type filter
            if (this.activeFilters.type.length > 0) {
                filtered = filtered.filter(lot => 
                    this.activeFilters.type.includes(lot.type)
                );
            }

            return filtered;
        },
        // Lots used for generating filter options (only type filter applied, no search)
        filterOptionsLots() {
            let filtered = this.allLots;

            // Type filter only
            if (this.activeFilters.type.length > 0) {
                filtered = filtered.filter(lot => 
                    this.activeFilters.type.includes(lot.type)
                );
            }

            return filtered;
        },
        // Filter options computed properties
        filteredLots() {
            let filtered = this.baseFilteredLots;

            // Price filter - only apply to lots with available status
            if (this.activeFilters.price.length > 0) {
                filtered = filtered.filter(lot => {
                    if (lot.status !== 'available') return false;
                    return this.activeFilters.price.some(priceRange => {
                        if (priceRange === 'POA') {
                            // Special case for POA - lots without a price
                            return !lot.price;
                        } else {
                            if (!lot.price) return false;
                            const [min, max] = priceRange.split('-').map(p => parseInt(p));
                            return lot.price >= min && lot.price <= max;
                        }
                    });
                });
            }

            // Bedrooms filter
            if (this.activeFilters.bedrooms.length > 0) {
                filtered = filtered.filter(lot => 
                    lot.bedrooms && this.activeFilters.bedrooms.includes(lot.bedrooms.toString())
                );
            }

            // Cars filter
            if (this.activeFilters.cars.length > 0) {
                filtered = filtered.filter(lot => 
                    lot.cars && this.activeFilters.cars.includes(lot.cars.toString())
                );
            }

            // Floor size filter
            if (this.activeFilters.floorSize.length > 0) {
                filtered = filtered.filter(lot => {
                    if (!lot.floorSize) return false;
                    return this.activeFilters.floorSize.some(sizeRange => {
                        const [min, max] = sizeRange.split('-').map(s => parseInt(s));
                        return lot.floorSize >= min && lot.floorSize <= max;
                    });
                });
            }

            // Section size filter
            if (this.activeFilters.sectionSize.length > 0) {
                filtered = filtered.filter(lot => {
                    if (!lot.sectionSize) return false;
                    return this.activeFilters.sectionSize.some(sizeRange => {
                        if (sizeRange === '1700-max') {
                            // Special case for Super lot - 1700m² and above
                            return lot.sectionSize >= 1700;
                        } else {
                            const [min, max] = sizeRange.split('-').map(s => parseInt(s));
                            return lot.sectionSize >= min && lot.sectionSize <= max;
                        }
                    });
                });
            }

            // Stage filter
            if (this.activeFilters.stage.length > 0) {
                filtered = filtered.filter(lot => 
                    lot.stage && this.activeFilters.stage.includes(lot.stage)
                );
            }

            // Builder filter
            if (this.activeFilters.builder.length > 0) {
                filtered = filtered.filter(lot => 
                    lot.builder && this.activeFilters.builder.includes(lot.builder)
                );
            }

            // Exclude sold lots only for Sections and House & Land (not for showHome)
            const selectedType = this.activeFilters.type[0];
            if (selectedType !== 'showHome') {
                filtered = filtered.filter(lot => lot.status !== 'sold');
            }

            // Sort the filtered results
            const statusPriority = {
                available: 0,
                underOffer: 1,
                sold: 2
            };

            filtered.sort((a, b) => {
                const aStatusRank = statusPriority[a.status] ?? Number.MAX_SAFE_INTEGER;
                const bStatusRank = statusPriority[b.status] ?? Number.MAX_SAFE_INTEGER;

                // Always prioritize available -> underOffer -> sold before field sorting
                if (aStatusRank !== bStatusRank) {
                    return aStatusRank - bStatusRank;
                }

                let aValue, bValue;
                let aHasValue, bHasValue;

                switch (this.sortField) {
                    case 'sort':
                        aValue = a.sort;
                        bValue = b.sort;
                        aHasValue = aValue !== null && aValue !== undefined;
                        bHasValue = bValue !== null && bValue !== undefined;
                        break;
                    case 'price':
                        aValue = a.price;
                        bValue = b.price;
                        aHasValue = aValue !== null && aValue !== undefined;
                        bHasValue = bValue !== null && bValue !== undefined;
                        break;
                    case 'sectionSize':
                        aValue = a.sectionSize;
                        bValue = b.sectionSize;
                        aHasValue = aValue !== null && aValue !== undefined;
                        bHasValue = bValue !== null && bValue !== undefined;
                        break;
                    case 'floorSize':
                        aValue = a.floorSize;
                        bValue = b.floorSize;
                        aHasValue = aValue !== null && aValue !== undefined;
                        bHasValue = bValue !== null && bValue !== undefined;
                        break;
                    default:
                        aValue = 0;
                        bValue = 0;
                        aHasValue = true;
                        bHasValue = true;
                }

                // Handle null/undefined values - place them at the end
                if (!aHasValue && !bHasValue) {
                    return 0; // Both are null/undefined, maintain order
                } else if (!aHasValue) {
                    return 1; // a is null/undefined, place it at the end
                } else if (!bHasValue) {
                    return -1; // b is null/undefined, place it at the end
                }

                // Both have values, sort normally
                if (this.sortDirection === 'asc') {
                    return aValue - bValue;
                } else {
                    return bValue - aValue;
                }
            });

            return filtered;
        },
        // Filter options computed properties
        priceOptions() {
            const prices = this.filterOptionsLots
                .filter(lot => lot.price && lot.status === 'available')
                .map(lot => lot.price)
                .sort((a, b) => a - b);

            const options = [];

            if (prices.length === 0) {
                // If no priced lots, only add POA if available
                const hasPOALots = this.filterOptionsLots.some(lot => 
                    lot.status === 'available' && !lot.price
                );
                
                if (hasPOALots) {
                    options.push({ value: 'POA', label: 'POA' });
                }
                return options;
            }

            const minPrice = Math.floor(prices[0] / 25000) * 25000;
            const maxPrice = Math.ceil(prices[prices.length - 1] / 25000) * 25000;

            for (let i = minPrice; i < maxPrice; i += 25000) {
                const range = `${i}-${i + 25000}`;
                const label = `$${(i / 1000).toFixed(0)}k - $${((i + 25000) / 1000).toFixed(0)}k`;
                options.push({ value: range, label });
            }

            // Add POA option at the end if there are lots without a price
            const hasPOALots = this.filterOptionsLots.some(lot => 
                lot.status === 'available' && !lot.price
            );
            
            if (hasPOALots) {
                options.push({ value: 'POA', label: 'POA' });
            }

            return options;
        },
        bedroomsOptions() {
            const bedrooms = [...new Set(this.filterOptionsLots
                .filter(lot => lot.bedrooms)
                .map(lot => lot.bedrooms)
                .sort((a, b) => a - b))];

            return bedrooms.map(bedroom => ({
                value: bedroom.toString(),
                label: `${bedroom} bedroom${bedroom > 1 ? 's' : ''}`
            }));
        },
        carsOptions() {
            const cars = [...new Set(this.filterOptionsLots
                .filter(lot => lot.cars)
                .map(lot => lot.cars)
                .sort((a, b) => a - b))];

            return cars.map(car => ({
                value: car.toString(),
                label: `${car} car${car > 1 ? 's' : ''}`
            }));
        },
        floorSizeOptions() {
            const sizes = this.filterOptionsLots
                .filter(lot => lot.floorSize)
                .map(lot => lot.floorSize)
                .sort((a, b) => a - b);

            if (sizes.length === 0) return [];

            const minSize = Math.floor(sizes[0] / 50) * 50;
            const maxSize = Math.ceil(sizes[sizes.length - 1] / 50) * 50;
            const options = [];

            for (let i = minSize; i < maxSize; i += 50) {
                const range = `${i}-${i + 50}`;
                const label = `${i}-${i + 50}m²`;
                options.push({ value: range, label });
            }

            return options;
        },
        sectionSizeOptions() {
            const sizes = this.filterOptionsLots
                .filter(lot => lot.sectionSize)
                .map(lot => lot.sectionSize)
                .sort((a, b) => a - b);

            if (sizes.length === 0) return [];

            const minSize = Math.floor(sizes[0] / 100) * 100;
            const maxSize = Math.ceil(sizes[sizes.length - 1] / 100) * 100;
            const options = [];

            // Generate all possible 100m² increments up to 700m²
            for (let i = minSize; i <= 700; i += 100) {
                const range = `${i}-${i + 100}`;
                const label = `${i}-${i + 100}m²`;
                
                // Check if this range actually has results
                const hasResults = this.filterOptionsLots.some(lot => {
                    if (!lot.sectionSize) return false;
                    const lotSize = parseInt(lot.sectionSize);
                    return lotSize >= i && lotSize < i + 100;
                });
                
                // Only add options that have results
                if (hasResults) {
                    options.push({ value: range, label });
                }
            }

            // Add "Super lot" option for sections 1700m² and above
            const superLotResults = this.filterOptionsLots.some(lot => {
                if (!lot.sectionSize) return false;
                const lotSize = parseInt(lot.sectionSize);
                return lotSize >= 1700;
            });
            
            if (superLotResults) {
                options.push({ value: '1700-max', label: 'Super lot' });
            }

            return options;
        },
        builderOptions() {
            if (this.useBuilderCatalog) {
                return this.allBuilders.map(b => ({
                    value: String(b.id),
                    label: b.title
                }));
            }
            const builders = [...new Set(this.filterOptionsLots
                .filter(lot => lot.builder)
                .map(lot => lot.builder)
                .sort())];

            return builders.map(builder => ({
                value: builder,
                label: builder
            }));
        },
        stageOptions() {
            const stages = [...new Set(this.filterOptionsLots
                .filter(lot => lot.stage)
                .map(lot => lot.stage)
                .sort())];

            return stages.map(stage => ({
                value: stage,
                label: stage
            }));
        },
        hasMoreLots() {
            if (this.useBuilderCatalog) {
                return this.displayedBuilders.length < this.filteredBuilders.length;
            }
            return this.displayedLots.length < this.filteredLots.length;
        },
        remainingLotsCount() {
            if (this.useBuilderCatalog) {
                return this.filteredBuilders.length - this.displayedBuilders.length;
            }
            return this.filteredLots.length - this.displayedLots.length;
        },
        visibleFilters() {
            if (this.useBuilderCatalog) {
                return ['builder'];
            }
            const selectedType = this.activeFilters.type[0];
            
            if (selectedType === 'sectionEmpty') {
                // For sections, show Type, Price, Section Size, and Stage filters
                // HIDE STAGE FILTERS FOR NOW
                return ['type', 'price', 'sectionSize'];
            } else if (selectedType === 'showHome') {
                // For show homes, hide price filter
                return ['type', 'bedrooms', 'cars', 'floorSize', 'sectionSize', 'builder'];
            } else {
                // For all other types (house & land), show all filters including stage
                return ['type', 'bedrooms', 'cars', 'price', 'floorSize', 'sectionSize', 'builder'];
            }
        },
        searchPlaceholder() {
            const selectedType = this.activeFilters.type[0];
            
            if (selectedType === 'sectionEmpty') {
                // For sections, keep as is
                return 'Type lot number here';
            } else if (selectedType === 'showHome') {
                // For show homes
                return 'Type address or builder name here';
            } else {
                // For house & land
                return 'Type lot number or builder name here';
            }
        },
    },
    methods: {
        /**
         * Single source of truth for "lot matches search" (partial match on title, builder, heading).
         * Used by list filter, map focus, and cross-type results so search behaves consistently.
         */
        lotMatchesSearch(lot, searchTerm) {
            if (!searchTerm || typeof searchTerm !== 'string') return true;
            const search = searchTerm.trim().toLowerCase();
            if (!search) return true;
            return (
                (lot.title && lot.title.toLowerCase().includes(search)) ||
                (lot.builder && lot.builder.toLowerCase().includes(search)) ||
                (lot.heading && lot.heading.toLowerCase().includes(search))
            );
        },
        renderViewFilterSection() {
            if (!this.showMap) return null
            return h('div', { class: 'view-filter-section' }, [
                h('label', { class: 'filter-label' }, 'View:'),
                h('div', { class: 'view-options' }, [
                    h('div', {
                        class: ['', { active: this.viewMode === 'grid' }],
                        onClick: () => this.toggleViewMode()
                    }, [
                        h('svg', {
                            xmlns: 'http://www.w3.org/2000/svg',
                            width: '20',
                            height: '20',
                            viewBox: '0 0 20 20'
                        }, [
                            h('g', { id: 'Group_16171', 'data-name': 'Group_16171', transform: 'translate(0 -0.013)' }, [
                                h('g', { id: 'Rectangle_11082', 'data-name': 'Rectangle_11082', transform: 'translate(0 0.013)', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
                                    h('rect', { width: '9', height: '9', stroke: 'none' }),
                                    h('rect', { x: '1', y: '1', width: '7', height: '7', fill: 'none' })
                                ]),
                                h('g', { id: 'Rectangle_11083', 'data-name': 'Rectangle_11083', transform: 'translate(11 0.013)', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
                                    h('rect', { width: '9', height: '9', stroke: 'none' }),
                                    h('rect', { x: '1', y: '1', width: '7', height: '7', fill: 'none' })
                                ]),
                                h('g', { id: 'Rectangle_11084', 'data-name': 'Rectangle_11084', transform: 'translate(0 11.013)', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
                                    h('rect', { width: '9', height: '9', stroke: 'none' }),
                                    h('rect', { x: '1', y: '1', width: '7', height: '7', fill: 'none' })
                                ]),
                                h('g', { id: 'Rectangle_11085', 'data-name': 'Rectangle_11085', transform: 'translate(11 11.013)', fill: 'none', stroke: 'currentColor', 'stroke-width': '2' }, [
                                    h('rect', { width: '9', height: '9', stroke: 'none' }),
                                    h('rect', { x: '1', y: '1', width: '7', height: '7', fill: 'none' })
                                ])
                            ])
                        ])
                    ]),
                    h('div', {
                        class: ['', { active: this.viewMode === 'map' }],
                        onClick: () => this.toggleViewMode()
                    }, [
                        h('svg', {
                            xmlns: 'http://www.w3.org/2000/svg',
                            width: '20.104',
                            height: '20.014',
                            viewBox: '0 0 20.104 20.014'
                        }, [
                            h('path', { id: 'map_24dp_E3E3E3_FILL0_wght300_GRAD0_opsz24', d: 'M153.6-798.446l-7.1-2.484-5.09,1.969a.97.97,0,0,1-.969-.116,1.014,1.014,0,0,1-.446-.871v-15.51a1.1,1.1,0,0,1,.188-.629,1.054,1.054,0,0,1,.527-.4l5.79-1.97,7.1,2.483,5.09-1.969a1,1,0,0,1,.969.1.961.961,0,0,1,.446.843v15.6a1.029,1.029,0,0,1-.2.635,1.164,1.164,0,0,1-.555.4Zm-.887-2.17v-13.837l-5.322-1.861v13.837Zm1.774,0,3.843-1.274V-815.9l-3.843,1.451ZM141.774-801l3.843-1.474v-13.837l-3.843,1.3Zm12.713-13.45v0Zm-8.869-1.861v0Z', transform: 'translate(-140 818.46)', fill: 'currentColor' })
                        ])
                    ])
                ])
            ]);
        },
        async fetchLots() {
            this.initPageMode();
            try {
                const lotsPromise = fetch('/all-lots.json');
                const buildersPromise = this.useBuilderCatalog ? fetch('/all-builders.json') : null;
                const lotsResponse = await lotsPromise;
                const lotsData = await lotsResponse.json();
                this.allLots = lotsData.data || [];
                if (buildersPromise) {
                    const buildersResponse = await buildersPromise;
                    const buildersData = await buildersResponse.json();
                    this.allBuilders = buildersData.data || [];
                } else {
                    this.allBuilders = [];
                }
                this.updateDisplayedLots();

                if (!this.useBuilderCatalog) {
                    this.$nextTick(async () => {
                        await this.waitForVisibleCardsToLoad();
                        this.preloadOtherTypes();
                    });
                }
            } catch (error) {
                console.error('Error fetching lots:', error);
            } finally {
                this.loading = false;
            }
        },
        toggleFilter(filterType, value) {
            if (filterType === 'builder' && this.useBuilderCatalog) {
                value = String(value);
            }
            // Ensure the filter type exists in activeFilters
            if (!this.activeFilters[filterType]) {
                this.activeFilters[filterType] = [];
            }
            
            // If changing type filter, clear section size filter since available sizes will change
            if (filterType === 'type') {
                this.activeFilters.sectionSize = [];
                // Reset sort to default if current sort field won't be visible for the new type
                if (this.sortField === 'floorSize' && value === 'sectionEmpty') {
                    this.sortField = 'sort';
                    this.sortDirection = 'asc';
                }
            }
            
            // Check if we're applying a filter (not removing it)
            const isApplyingFilter = !this.activeFilters[filterType].includes(value);
            
            // For single selection, replace the entire array with the selected value
            if (this.activeFilters[filterType].includes(value)) {
                // If already selected, deselect it
                this.activeFilters[filterType] = [];
            } else {
                // Select only this value (single selection)
                this.activeFilters[filterType] = [value];
            }
            
            // Switch to grid view when applying a filter
            if (isApplyingFilter) {
                this.viewMode = 'grid';
            }
            
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            // Close the dropdown after selection
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearAllFilters() {
            this.activeFilters = {
                type: [],
                price: [],
                bedrooms: [],
                cars: [],
                floorSize: [],
                sectionSize: [],
                builder: [],
                stage: []
            };
            this.searchTerm = '';
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
        },
        resetFiltersExceptType() {
            // Keep the current type filter
            const currentType = this.activeFilters.type;
            
            // Reset all other filters
            this.activeFilters = {
                type: currentType, // Preserve type filter
                price: [],
                bedrooms: [],
                cars: [],
                floorSize: [],
                sectionSize: [],
                builder: [],
                stage: []
            };
            this.searchTerm = '';
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            if (this.mobileFiltersOpen) this.updateMobileFilterActiveStates();
        },
        updateURL() {
            const url = new URL(window.location);
            const params = url.searchParams;
            
            // Clear existing filter params
            ['price', 'bedrooms', 'cars', 'floorSize', 'sectionSize', 'stage', 'builder', 'search', 'sortField', 'sortDirection'].forEach(param => {
                params.delete(param);
            });
            
            // Add active filter params
            Object.keys(this.activeFilters).forEach(filterKey => {
                if (filterKey !== 'type' && this.activeFilters[filterKey] && this.activeFilters[filterKey].length > 0) {
                    params.set(filterKey, this.activeFilters[filterKey].join(','));
                }
            });
            
            // Add search term
            if (this.searchTerm) {
                params.set('search', this.searchTerm);
            }
            
            
            // Add sort parameters
            if (this.sortField && this.sortField !== 'sort') {
                params.set('sortField', this.sortField);
            }
            if (this.sortDirection && this.sortDirection !== 'asc') {
                params.set('sortDirection', this.sortDirection);
            }
            

            
            // Update URL without reloading the page
            window.history.replaceState({}, '', url.toString());
        },
        loadFiltersFromURL() {
            const url = new URL(window.location);
            const params = url.searchParams;
            
            // Load filter values from URL
            Object.keys(this.activeFilters).forEach(filterKey => {
                if (filterKey !== 'type' && params.has(filterKey)) {
                    const value = params.get(filterKey);
                    this.activeFilters[filterKey] = value ? value.split(',') : [];
                }
            });
            
            // Load search term
            if (params.has('search')) {
                this.searchTerm = params.get('search');
            }
            
            
            // Load sort parameters
            if (params.has('sortField')) {
                this.sortField = params.get('sortField');
            }
            if (params.has('sortDirection')) {
                this.sortDirection = params.get('sortDirection');
            }
            
            // Check if any filters are active (excluding type) and switch to grid view
            const hasActiveFilters = Object.keys(this.activeFilters).some(filterKey => {
                if (filterKey === 'type') return false;
                return this.activeFilters[filterKey] && this.activeFilters[filterKey].length > 0;
            });
            
            // Check if search term exists and we're on mobile
            const hasSearchOnMobile = this.windowWidth < 768 && this.searchTerm && this.searchTerm.trim() !== '';
            
            if (hasActiveFilters || hasSearchOnMobile) {
                this.viewMode = 'grid';
            }

        },
        clearTypeFilter() {
            this.activeFilters.type = [];
            // Clear section size filter since available sizes will change
            this.activeFilters.sectionSize = [];
            this.currentPage = 1;
            
            // Set view mode to grid when clearing type filter (Show all)
            this.viewMode = 'grid';
            
            this.updateDisplayedLots();
            this.updateURL();
            // Close the dropdown after clearing
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearPriceFilter() {
            this.activeFilters.price = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearBedroomsFilter() {
            this.activeFilters.bedrooms = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearCarsFilter() {
            this.activeFilters.cars = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearFloorSizeFilter() {
            this.activeFilters.floorSize = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearBuilderFilter() {
            this.activeFilters.builder = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearStageFilter() {
            this.activeFilters.stage = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        clearSectionSizeFilter() {
            this.activeFilters.sectionSize = [];
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.closeDropdown();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        setSort(field, direction = 'asc') {
            this.sortField = field;
            this.sortDirection = direction;
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            this.sortDropdownOpen = false;
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        getSortFieldLabel() {
            const labels = {
                'sort': 'Lot number',
                'price': this.sortDirection === 'asc' ? 'Price (low to high)' : 'Price (high to low)',
                'sectionSize': this.sortDirection === 'asc' ? 'Section size (small to large)' : 'Section size (large to small)',
                'floorSize': this.sortDirection === 'asc' ? 'Floor size (small to large)' : 'Floor size (large to small)'
            };
            return labels[this.sortField] || this.sortField;
        },
        toggleSortDropdown() {
            this.sortDropdownOpen = !this.sortDropdownOpen;
            // Close other dropdowns when opening sort dropdown
            if (this.sortDropdownOpen) {
                this.openDropdown = null;
            }
        },
        toggleSortDirection() {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            this.currentPage = 1;
            this.updateDisplayedLots();
            this.updateURL();
            
            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        toggleViewMode() {
            this.viewMode = this.viewMode === 'grid' ? 'map' : 'grid';
            this.updateURL();

            // Update mobile filter active states if overlay is open
            if (this.mobileFiltersOpen) {
                this.updateMobileFilterActiveStates();
            }
        },
        toggleMapVisibility() {
            const mapComponent = document.querySelector('.map-component');
            if (mapComponent) {
                if (this.viewMode === 'map') {
                    mapComponent.classList.add('show');
                } else {
                    mapComponent.classList.remove('show');
                }
            }
        },
        toggleMobileFilters() {
            this.mobileFiltersOpen = !this.mobileFiltersOpen;
            // Prevent body scroll when popup is open
            if (this.mobileFiltersOpen) {
                document.querySelector('body').setAttribute('data-modal-expanded', 'true');
            } else {
                document.querySelector('body').setAttribute('data-modal-expanded', 'false');
            }
        },
        getSelectedTypeLabel() {
            if (this.activeFilters.type.length === 0) {
                return 'Show all';
            } else if (this.activeFilters.type.includes('sectionEmpty')) {
                return 'Sections';
            } else if (this.activeFilters.type.includes('houseLand')) {
                return 'House & Land';
            } else if (this.activeFilters.type.includes('showHome')) {
                return 'Show homes';
            }
            return 'Show all';
        },
        toggleMobileFilterDropdown() {
            this.mobileFilterDropdownOpen = !this.mobileFilterDropdownOpen;
        },
        closeMobileFilters() {
            this.mobileFiltersOpen = false;
            document.querySelector('body').setAttribute('data-modal-expanded', 'false');
        },
        getActiveFilterCount() {
            let count = 0;
            
            // Count active filters (excluding type filter)
            Object.keys(this.activeFilters).forEach(filterKey => {
                if (filterKey !== 'type' && this.activeFilters[filterKey] && this.activeFilters[filterKey].length > 0) {
                    count++;
                }
            });
            
            // Add search term
            if (this.searchTerm) {
                count++;
            }
            
            // Add sort field (if not default)
            if (this.sortField && this.sortField !== 'sort') {
                count++;
            }
            
            // Add sort direction (if not default)
            if (this.sortDirection && this.sortDirection !== 'asc') {
                count++;
            }
            
            return count;
        },
        
        getRelevantFilterCount() {
            let count = 0;
            
            // Count only the filters that are shown in mobile popup (excluding type, view, and sort)
            Object.keys(this.activeFilters).forEach(filterKey => {
                if (filterKey !== 'type' && this.activeFilters[filterKey] && this.activeFilters[filterKey].length > 0) {
                    count++;
                }
            });
            
            // Add search term
            if (this.searchTerm) {
                count++;
            }
            
            return count;
        },
        updateMobileFilterActiveStates() {
            
            // Update builder filter buttons
            const builderButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'builder\'"]');
            builderButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('builder', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.builder && this.activeFilters.builder.includes(value));
                }
            });
            
            // Update "Any" button for builder filter
            const anyBuilderButton = document.querySelector('.mobile-filter-option[onclick*="clearBuilderFilter"]');
            if (anyBuilderButton) {
                anyBuilderButton.classList.toggle('active', !this.activeFilters.builder || this.activeFilters.builder.length === 0);
            }
            
            // Update stage filter buttons
            const stageButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'stage\'"]');
            stageButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('stage', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.stage && this.activeFilters.stage.includes(value));
                }
            });
            
            // Update "Any" button for stage filter
            const anyStageButton = document.querySelector('.mobile-filter-option[onclick*="clearStageFilter"]');
            if (anyStageButton) {
                anyStageButton.classList.toggle('active', !this.activeFilters.stage || this.activeFilters.stage.length === 0);
            }
            
            // Update bedrooms filter buttons
            const bedroomsButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'bedrooms\'"]');
            bedroomsButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('bedrooms', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.bedrooms && this.activeFilters.bedrooms.includes(value));
                }
            });
            
            // Update "Any" button for bedrooms filter
            const anyBedroomsButton = document.querySelector('.mobile-filter-option[onclick*="clearBedroomsFilter"]');
            if (anyBedroomsButton) {
                anyBedroomsButton.classList.toggle('active', !this.activeFilters.bedrooms || this.activeFilters.bedrooms.length === 0);
            }
            
            // Update cars filter buttons
            const carsButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'cars\'"]');
            carsButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('cars', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.cars && this.activeFilters.cars.includes(value));
                }
            });
            
            // Update "Any" button for cars filter
            const anyCarsButton = document.querySelector('.mobile-filter-option[onclick*="clearCarsFilter"]');
            if (anyCarsButton) {
                anyCarsButton.classList.toggle('active', !this.activeFilters.cars || this.activeFilters.cars.length === 0);
            }
            
            // Update price filter buttons
            const priceButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'price\'"]');
            priceButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('price', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.price && this.activeFilters.price.includes(value));
                }
            });
            
            // Update "Any" button for price filter
            const anyPriceButton = document.querySelector('.mobile-filter-option[onclick*="clearPriceFilter"]');
            if (anyPriceButton) {
                anyPriceButton.classList.toggle('active', !this.activeFilters.price || this.activeFilters.price.length === 0);
            }
            
            // Update section size filter buttons
            const sectionSizeButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'sectionSize\'"]');
            sectionSizeButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('sectionSize', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.sectionSize && this.activeFilters.sectionSize.includes(value));
                }
            });
            
            // Update "Any" button for section size filter
            const anySectionSizeButton = document.querySelector('.mobile-filter-option[onclick*="clearSectionSizeFilter"]');
            if (anySectionSizeButton) {
                anySectionSizeButton.classList.toggle('active', !this.activeFilters.sectionSize || this.activeFilters.sectionSize.length === 0);
            }
            
            // Update floor size filter buttons
            const floorSizeButtons = document.querySelectorAll('.mobile-filter-option[onclick*="toggleFilter(\'floorSize\'"]');
            floorSizeButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                const match = onclick.match(/toggleFilter\('floorSize', '([^']+)'\)/);
                if (match) {
                    const value = match[1];
                    button.classList.toggle('active', this.activeFilters.floorSize && this.activeFilters.floorSize.includes(value));
                }
            });
            
            // Update "Any" button for floor size filter
            const anyFloorSizeButton = document.querySelector('.mobile-filter-option[onclick*="clearFloorSizeFilter"]');
            if (anyFloorSizeButton) {
                anyFloorSizeButton.classList.toggle('active', !this.activeFilters.floorSize || this.activeFilters.floorSize.length === 0);
            }
            
            // Update sort buttons
            const sortButtons = document.querySelectorAll('.mobile-filter-option[onclick*="setSort"]');
            sortButtons.forEach(button => {
                const onclick = button.getAttribute('onclick');
                if (onclick.includes('setSort(\'sort\', \'asc\')')) {
                    button.classList.toggle('active', this.sortField === 'sort');
                } else if (onclick.includes('setSort(\'price\', \'asc\')')) {
                    button.classList.toggle('active', this.sortField === 'price' && this.sortDirection === 'asc');
                } else if (onclick.includes('setSort(\'price\', \'desc\')')) {
                    button.classList.toggle('active', this.sortField === 'price' && this.sortDirection === 'desc');
                } else if (onclick.includes('setSort(\'sectionSize\', \'asc\')')) {
                    button.classList.toggle('active', this.sortField === 'sectionSize' && this.sortDirection === 'asc');
                } else if (onclick.includes('setSort(\'sectionSize\', \'desc\')')) {
                    button.classList.toggle('active', this.sortField === 'sectionSize' && this.sortDirection === 'desc');
                } else if (onclick.includes('setSort(\'floorSize\', \'asc\')')) {
                    button.classList.toggle('active', this.sortField === 'floorSize' && this.sortDirection === 'asc');
                } else if (onclick.includes('setSort(\'floorSize\', \'desc\')')) {
                    button.classList.toggle('active', this.sortField === 'floorSize' && this.sortDirection === 'desc');
                }
            });
            
        },
        
        renderMobileOverlay(show) {
            // Remove existing overlay
            const existingOverlay = document.getElementById('mobile-filters-overlay');
            if (existingOverlay) {
                existingOverlay.remove();
            }
            
            if (show) {
                // Create overlay element
                const overlay = document.createElement('div');
                overlay.id = 'mobile-filters-overlay';
                overlay.className = 'mobile-filters-overlay';
                
                // Build filter content HTML
                let filterContent = '';

                // Stage filter
                if (this.visibleFilters.includes('stage')) {
                    filterContent += `
                        <div class="mobile-filter-group">
                            <label class="mobile-filter-label">Precinct</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.stage && this.activeFilters.stage.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearStageFilter()">Any</button>
                                ${this.stageOptions.map(stage => `
                                    <button class="mobile-filter-option ${this.activeFilters.stage && this.activeFilters.stage.includes(stage.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('stage', '${stage.value}')">${stage.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                // Building providers filter
                if (this.visibleFilters.includes('builder')) {
                    filterContent += `
                        <div class="mobile-filter-group${this.useBuilderCatalog ? ' mobile-filter-group--builder-catalog' : ''}">
                            <label class="mobile-filter-label">${this.useBuilderCatalog ? 'Select Builder' : 'Builder'}</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.builder && this.activeFilters.builder.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearBuilderFilter()">Any</button>
                                ${this.builderOptions.map(builder => `
                                    <button class="mobile-filter-option ${this.activeFilters.builder && this.activeFilters.builder.includes(builder.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('builder', '${builder.value}')">${builder.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                // Bedrooms filter
                if (this.visibleFilters.includes('bedrooms')) {
                    filterContent += `
                        <div class="mobile-filter-group">
                            <label class="mobile-filter-label">Bedrooms</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.bedrooms && this.activeFilters.bedrooms.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearBedroomsFilter()">Any</button>
                                ${this.bedroomsOptions.map(bedroom => `
                                    <button class="mobile-filter-option ${this.activeFilters.bedrooms && this.activeFilters.bedrooms.includes(bedroom.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('bedrooms', '${bedroom.value}')">${bedroom.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                // Cars filter
                if (this.visibleFilters.includes('cars')) {
                    filterContent += `
                        <div class="mobile-filter-group">
                            <label class="mobile-filter-label">Cars</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.cars && this.activeFilters.cars.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearCarsFilter()">Any</button>
                                ${this.carsOptions.map(car => `
                                    <button class="mobile-filter-option ${this.activeFilters.cars && this.activeFilters.cars.includes(car.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('cars', '${car.value}')">${car.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                // Floor size filter
                if (this.visibleFilters.includes('floorSize')) {
                    filterContent += `
                        <div class="mobile-filter-group">
                            <label class="mobile-filter-label">Floor size</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.floorSize && this.activeFilters.floorSize.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearFloorSizeFilter()">Any</button>
                                ${this.floorSizeOptions.map(size => `
                                    <button class="mobile-filter-option ${this.activeFilters.floorSize && this.activeFilters.floorSize.includes(size.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('floorSize', '${size.value}')">${size.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                // Section size filter
                if (this.visibleFilters.includes('sectionSize')) {
                    filterContent += `
                        <div class="mobile-filter-group">
                            <label class="mobile-filter-label">Section size</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.sectionSize && this.activeFilters.sectionSize.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearSectionSizeFilter()">Any</button>
                                ${this.sectionSizeOptions.map(size => `
                                    <button class="mobile-filter-option ${this.activeFilters.sectionSize && this.activeFilters.sectionSize.includes(size.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('sectionSize', '${size.value}')">${size.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }

                // Price filter
                if (this.visibleFilters.includes('price')) {
                    filterContent += `
                        <div class="mobile-filter-group">
                            <label class="mobile-filter-label">Price</label>
                            <div class="mobile-filter-options">
                                <button class="mobile-filter-option ${this.activeFilters.price && this.activeFilters.price.length === 0 ? 'active' : ''}" onclick="window.lotFilterApp.clearPriceFilter()">Any</button>
                                ${this.priceOptions.map(price => `
                                    <button class="mobile-filter-option ${this.activeFilters.price && this.activeFilters.price.includes(price.value) ? 'active' : ''}" onclick="window.lotFilterApp.toggleFilter('price', '${price.value}')">${price.label}</button>
                                `).join('')}
                            </div>
                        </div>
                    `;
                }


                // Sort by options (not on builder catalog)
                if (!this.useBuilderCatalog) {
                    filterContent += `
                    <div class="mobile-filter-group">
                        <label class="mobile-filter-label">Sort by</label>
                        <div class="mobile-filter-options">
                            <button class="mobile-filter-option ${this.sortField === 'sort' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('sort', 'asc')">Lot number</button>
                            ${this.visibleFilters.includes('price') ? `<button class="mobile-filter-option ${this.sortField === 'price' && this.sortDirection === 'asc' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('price', 'asc')">Price (low to high)</button>` : ''}
                            ${this.visibleFilters.includes('price') ? `<button class="mobile-filter-option ${this.sortField === 'price' && this.sortDirection === 'desc' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('price', 'desc')">Price (high to low)</button>` : ''}
                            <button class="mobile-filter-option ${this.sortField === 'sectionSize' && this.sortDirection === 'asc' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('sectionSize', 'asc')">Section size (small to large)</button>
                            <button class="mobile-filter-option ${this.sortField === 'sectionSize' && this.sortDirection === 'desc' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('sectionSize', 'desc')">Section size (large to small)</button>
                            ${this.visibleFilters.includes('floorSize') ? `<button class="mobile-filter-option ${this.sortField === 'floorSize' && this.sortDirection === 'asc' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('floorSize', 'asc')">Floor size (small to large)</button>` : ''}
                            ${this.visibleFilters.includes('floorSize') ? `<button class="mobile-filter-option ${this.sortField === 'floorSize' && this.sortDirection === 'desc' ? 'active' : ''}" onclick="window.lotFilterApp.setSort('floorSize', 'desc')">Floor size (large to small)</button>` : ''}
                        </div>
                    </div>
                `;
                }

                overlay.innerHTML = `
                    <div class="mobile-filters-popup">
                        <div class="mobile-filters-header">
                            <h3>All Filters</h3>
                            <button class="close-btn" onclick="window.lotFilterApp.closeMobileFilters()">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div class="mobile-filters-content">
                            ${filterContent}
                        </div>
                        <div class="mobile-filters-footer">
                            <button class="btn btn-secondary" onclick="window.lotFilterApp.resetFiltersExceptType()">Reset</button>
                            <button class="btn btn-primary" onclick="window.lotFilterApp.closeMobileFilters()">Apply</button>
                        </div>
                    </div>
                `;
                
                // Add click handler for overlay background
                overlay.addEventListener('click', (e) => {
                    if (e.target.classList.contains('mobile-filters-overlay')) {
                        this.closeMobileFilters();
                    }
                });
                
                // Append to body
                document.body.appendChild(overlay);
            }
        },
        setInitialTypeFilter() {
            const lotFilterApp = document.getElementById('lot-filter-app');
            if (lotFilterApp) {
                const dataType = lotFilterApp.getAttribute('data-type');
                this.viewMode = 'grid'; // List only (no map in this component)
                switch (dataType) {
                    case 'hlListing':
                        this.activeFilters.type = ['houseLand'];
                        break;
                    case 'shListing':
                        this.activeFilters.type = ['showHome'];
                        break;
                    default:
                        this.activeFilters.type = ['sectionEmpty'];
                        this.viewMode = this.showMap ? 'map' : 'grid'; // Sections default to map only when showMap
                        break;
                }
            }
        },
        
        preloadOtherTypes() {
            if (this.useBuilderCatalog) {
                return;
            }
            // Get the current active type
            const currentType = this.activeFilters.type[0];
            
            // Define all available types
            const allTypes = ['sectionEmpty', 'houseLand', 'showHome'];
            
            // Add a small delay to ensure visible cards have priority
            setTimeout(() => {
                console.log('Starting preload of other types (lower priority)');
                // Preload first batch for other types
                allTypes.forEach(type => {
                    if (type !== currentType) {
                        this.preloadTypeFirstBatch(type);
                    }
                });
            }, 500); // 500ms delay to prioritize visible cards
        },
        
        preloadTypeFirstBatch(type) {
            if (this.useBuilderCatalog) {
                return;
            }
            // Temporarily set the type filter to get filtered lots for this type
            const originalType = this.activeFilters.type;
            this.activeFilters.type = [type];
            
            // Get the first batch of lots for this type
            const firstBatchLots = this.filteredLots.slice(0, this.itemsPerPage);
            
            // Preload cards for the first batch
            firstBatchLots.forEach(lot => {
                if (!this.isCardLoadedOrLoading(lot.id)) {
                    this.preloadCard(lot.id);
                }
            });
            
            // Restore the original type filter
            this.activeFilters.type = originalType;
        },
        updateDisplayedLots() {
            if (this.useBuilderCatalog) {
                this.displayedBuilders = this.filteredBuilders.slice(0, this.currentPage * this.itemsPerPage);
                return;
            }
            this.displayedLots = this.filteredLots.slice(0, this.currentPage * this.itemsPerPage);

            this.preloadVisibleCards();

            this.preloadNextBatch();
        },
        loadMore() {
            this.currentPage++;
            this.updateDisplayedLots();
        },
        preloadVisibleCards() {
            if (this.useBuilderCatalog) {
                return;
            }
            // Preload cards for the currently visible lots (highest priority)
            console.log(`Preloading ${this.displayedLots.length} visible cards (highest priority)`);
            this.displayedLots.forEach(lot => {
                if (!this.isCardLoadedOrLoading(lot.id)) {
                    this.preloadCard(lot.id);
                }
            });
        },
        async waitForVisibleCardsToLoad() {
            // Wait for visible cards to be loaded before proceeding with other preloading
            const visibleLotIds = this.displayedLots.map(lot => lot.id);
            const maxWaitTime = 3000; // 3 seconds max wait
            const checkInterval = 100; // Check every 100ms
            let elapsedTime = 0;
            
            while (elapsedTime < maxWaitTime) {
                // Check if all visible cards are loaded
                const allVisibleCardsLoaded = visibleLotIds.every(id => this.preloadedCards.has(id));
                
                if (allVisibleCardsLoaded) {
                    console.log('All visible cards loaded, proceeding with other preloading');
                    return;
                }
                
                // Wait a bit and check again
                await new Promise(resolve => setTimeout(resolve, checkInterval));
                elapsedTime += checkInterval;
            }
            
            console.log('Timeout reached, proceeding with other preloading');
        },
        preloadNextBatch() {
            if (this.useBuilderCatalog) {
                return;
            }
            const nextBatchStart = this.currentPage * this.itemsPerPage;
            const nextBatchEnd = nextBatchStart + this.itemsPerPage;
            const nextBatchLots = this.filteredLots.slice(nextBatchStart, nextBatchEnd);
            
            // Preload cards for the next batch
            nextBatchLots.forEach(lot => {
                if (!this.isCardLoadedOrLoading(lot.id)) {
                    this.preloadCard(lot.id);
                }
            });
        },
        isCardLoadedOrLoading(lotId) {
            return this.preloadedCards.has(lotId) || this.loadingCards.has(lotId);
        },
        async preloadCard(lotId) {
            // Check if already loaded or currently loading
            if (this.isCardLoadedOrLoading(lotId)) {
                return;
            }
            
            // Mark as loading to prevent duplicate requests
            this.loadingCards.add(lotId);
            
            try {
                // console.log(`Loading card for lot ${lotId}`);
                const response = await fetch(`/section-card/${lotId}`);
                const html = await response.text();
                
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const cardElement = doc.querySelector('.section-card');
                
                if (cardElement) {
                    this.preloadedCards.set(lotId, cardElement.outerHTML);
                    // console.log(`Successfully loaded card for lot ${lotId}`);
                }
            } catch (error) {
                console.error(`Error preloading card for lot ${lotId}:`, error);
            } finally {
                // Remove from loading set
                this.loadingCards.delete(lotId);
            }
        },
        getLotTypeLabel(type) {
            const typeMap = {
                'sectionEmpty': 'Section',
                'houseLand': 'House & Land Package',
                'showHome': 'Show Home'
            };
            return typeMap[type] || type;
        },
        getStatusLabel(status) {
            const statusMap = {
                'available': 'Available',
                'notAvailable': 'Not Available',
                'sold': 'Sold',
                'underOffer': 'Under Offer'
            };
            return statusMap[status] || status;
        },
        toggleDropdown(dropdownName) {
            this.openDropdown = this.openDropdown === dropdownName ? null : dropdownName;
        },
        closeDropdown() {
            this.openDropdown = null;
            this.sortDropdownOpen = false;
        },
        getFilterLabel(filterType) {
            if (filterType === 'builder' && this.useBuilderCatalog) {
                return 'Select Builder';
            }
            const labels = {
                'type': 'Type',
                'bedrooms': 'Bedrooms',
                'cars': 'Cars',
                'price': 'Price',
                'floorSize': 'Floor size',
                'sectionSize': 'Section size',
                'stage': 'Precinct',
                'builder': 'Builder'
            };
            return labels[filterType] || filterType;
        },
        getActiveFilterCount(filterType) {
            return this.activeFilters[filterType] ? this.activeFilters[filterType].length : 0;
        },
        getSelectedOptionLabel(filterType) {
            if (!this.activeFilters[filterType] || this.activeFilters[filterType].length === 0) return '';
            
            const selectedValue = this.activeFilters[filterType][0];
            
            switch (filterType) {
                case 'type':
                    const typeLabels = {
                        'sectionEmpty': 'Sections',
                        'houseLand': 'House & Land',
                        'showHome': 'Show home'
                    };
                    return typeLabels[selectedValue] || selectedValue;
                case 'bedrooms':
                    return `${selectedValue}`;
                case 'cars':
                    return `${selectedValue}`;
                case 'price':
                    return this.priceOptions.find(option => option.value === selectedValue)?.label || selectedValue;
                case 'floorSize':
                    return this.floorSizeOptions.find(option => option.value === selectedValue)?.label || selectedValue;
                case 'sectionSize':
                    return this.sectionSizeOptions.find(option => option.value === selectedValue)?.label || selectedValue;
                case 'stage':
                    return selectedValue;
                case 'builder':
                    if (this.useBuilderCatalog) {
                        const row = this.allBuilders.find(b => String(b.id) === String(selectedValue));
                        return row ? row.title : selectedValue;
                    }
                    return selectedValue;
                default:
                    return selectedValue;
            }
        },
        initPageMode() {
            const el = document.getElementById('lot-filter-app');
            if (!el) {
                return;
            }
            this.pageEntryType = el.getAttribute('data-type') || '';
            const modeAttr = (el.getAttribute('data-hl-catalog-mode') || '').toLowerCase();
            if (this.pageEntryType === 'hlListing' && modeAttr !== 'lots') {
                this.hlCatalogMode = 'builders';
            } else {
                this.hlCatalogMode = 'lots';
            }
        },
        renderBuilderListingCard(b) {
            const hasLink = typeof b.linkUrl === 'string' && b.linkUrl.trim() !== '';

            const imageBlock = b.imageUrl
                ? h('div', { class: 'image-container' }, [
                    h('img', {
                        src: b.imageUrl,
                        alt: b.imageAlt || b.title || '',
                        loading: 'lazy'
                    })
                ])
                : h('div', { class: 'builder-card-image-placeholder' });

            const logoBlock = b.logoUrl
                ? h('div', { class: 'block-logo' }, [
                    h('div', { class: 'list-block' }, [
                        b.logoIsSvg
                            ? h('img', { src: b.logoUrl, alt: `${b.title} logo` })
                            : h('img', { src: b.logoUrl, alt: `${b.title} logo`, loading: 'lazy' })
                    ])
                ])
                : null;

            const arrow = hasLink
                ? h('span', { class: 'builder-external-card__arrow', 'aria-hidden': 'true' }, [
                    h('svg', {
                        xmlns: 'http://www.w3.org/2000/svg',
                        width: '24.527',
                        height: '18.198',
                        viewBox: '0 0 24.527 18.198'
                    }, [
                        h('g', { transform: 'translate(0 0.707)' }, [
                            h('path', {
                                d: 'M0,0H11.868V11.868',
                                transform: 'translate(14.721 0) rotate(45)',
                                fill: 'none',
                                stroke: '#74895e',
                                'stroke-width': '2'
                            }),
                            h('line', {
                                x1: '22.318',
                                transform: 'translate(0 8.431)',
                                fill: 'none',
                                stroke: '#74895e',
                                'stroke-width': '2'
                            })
                        ])
                    ])
                ])
                : null;

            const inner = [
                h('div', { class: 'card-image' }, [imageBlock, logoBlock]),
                h('div', { class: 'card-info builder-external-card__info' }, [
                    h('div', { class: 'card-row-title builder-external-card__footer' }, [
                        h('span', { class: 'card-title' }, b.title),
                        arrow
                    ])
                ])
            ];

            if (hasLink) {
                return h('a', {
                    class: 'section-card house-land-card builder-external-card',
                    href: b.linkUrl.trim(),
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    key: b.id
                }, inner);
            }
            return h('div', {
                class: 'section-card house-land-card builder-external-card builder-external-card--no-link',
                key: b.id
            }, inner);
        },
        formatPrice(price) {
            return price.toLocaleString();
        },
        async loadCardHtml(lotId, container) {
            // Check if card is already preloaded
            if (this.preloadedCards.has(lotId)) {
                container.innerHTML = this.preloadedCards.get(lotId);
                container.dataset.loaded = 'true';
                return;
            }
            
            // Check if card is currently being loaded by preloading system
            if (this.loadingCards.has(lotId)) {
                console.log(`Card ${lotId} is being loaded by preloading system, waiting...`);
                // Wait for the preloading to complete
                let waitTime = 0;
                const maxWaitTime = 5000; // 5 seconds max wait
                while (this.loadingCards.has(lotId) && waitTime < maxWaitTime) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    waitTime += 100;
                }
                
                // Check again if it's now loaded
                if (this.preloadedCards.has(lotId)) {
                    container.innerHTML = this.preloadedCards.get(lotId);
                    container.dataset.loaded = 'true';
                    return;
                }
            }
            
            // If not preloaded and not loading, load it now
            try {
                // console.log(`Loading card for display: ${lotId}`);
                const response = await fetch(`/section-card/${lotId}`);
                const html = await response.text();
                
                // Extract the card HTML from the response
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const cardElement = doc.querySelector('.section-card');
                
                if (cardElement) {
                    const cardHtml = cardElement.outerHTML;
                    container.innerHTML = cardHtml;
                    container.dataset.loaded = 'true';
                    // Cache the card for future use
                    this.preloadedCards.set(lotId, cardHtml);
                    // console.log(`Successfully loaded card for display: ${lotId}`);
                } else {
                    container.innerHTML = `<div class="error-card">Card not found ${lotId}</div>`;
                }
            } catch (error) {
            console.error(`Error loading card for lot ${lotId}:`, error);
                container.innerHTML = '<div class="error-card">Error loading card</div>';
            }
        },
    },
    watch: {
        filteredLots() {
            this.updateDisplayedLots();
        },
        filteredBuilders() {
            if (this.useBuilderCatalog) {
                this.updateDisplayedLots();
            }
        },
        'activeFilters.type'() {
            // Clear other filters when type changes
            this.activeFilters.price = [];
            this.activeFilters.bedrooms = [];
            this.activeFilters.cars = [];
            this.activeFilters.floorSize = [];
            this.activeFilters.sectionSize = [];
            this.activeFilters.builder = [];
            this.currentPage = 1;
            
            // Set view mode based on type
            if (this.activeFilters.type.includes('sectionEmpty')) {
                this.viewMode = this.showMap ? 'map' : 'grid'; // Sections default to map only when showMap
            } else {
                this.viewMode = 'grid'; // Other types default to grid
            }
            
            this.updateDisplayedLots();
        }
    },
    mounted() {
        const el = document.getElementById('lot-filter-app')
        const val = el?.getAttribute('data-show-map')
        this.showMap = val !== 'false' && val !== '0' && val !== ''

        this.initPageMode();
        this.fetchLots();
        this.setInitialTypeFilter();
        // Load filters from URL after initial setup
        this.$nextTick(() => {
            this.loadFiltersFromURL();
        });
        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.filter-dropdown') && !e.target.closest('.sort-dropdown')) {
                this.closeDropdown();
            }
        });
        // Watch for mobile filters state changes to render overlay
        this.$watch('mobileFiltersOpen', (newVal) => {
            this.renderMobileOverlay(newVal);
        });
        
        // Add resize listener to update window width
        this.handleResize = () => {
            this.windowWidth = window.innerWidth;
        };
        window.addEventListener('resize', this.handleResize);
    },
    unmounted() {
        // Clean up resize listener
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize);
        }
    },
    render() {
        if (this.loading) {
            return h('div', { class: 'lot-filter-loading' }, [
                h('div', { class: 'loading-spinner' }),
                h('p', this.useBuilderCatalog ? 'Loading builders...' : 'Loading lots...')
            ]);
        }

        const ub = this.useBuilderCatalog;

        return h('div', [
            // Mobile-only: type switch + view switch row -- HIDE FILTERS FOR NOW
            /* h('div', { class: 'mobile-type-view-row' }, [
                // Mobile Filter Dropdown
                h('div', { class: 'mobile-filter-dropdown' }, [
                h('div', { 
                    class: 'mobile-filter-dropdown-header',
                    onClick: () => this.toggleMobileFilterDropdown()
                }, [
                    h('label', { class: 'mobile-filter-dropdown-label' }, 'FILTER BY:'),
                    h('div', { class: 'mobile-filter-dropdown-toggle' }, [
                        h('span', { class: 'mobile-filter-dropdown-text' }, this.getSelectedTypeLabel()),
                        h('svg', {
                            class: ['mobile-filter-dropdown-chevron', { 'rotated': this.mobileFilterDropdownOpen }],
                            xmlns: 'http://www.w3.org/2000/svg',
                            width: '9.717',
                            height: '6',
                            viewBox: '0 0 9.717 6'
                        }, [
                            h('path', { 
                                d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                fill: '#2f412f', 
                                'fill-rule': 'evenodd' 
                            })
                        ])
                    ])
                ]),
                    h('div', { 
                        class: ['mobile-filter-dropdown-content', { 'show': this.mobileFilterDropdownOpen }]
                    }, [
                        h('div', { class: 'mobile-filter-dropdown-options' }, [
                            h('div', {
                                class: ['mobile-filter-dropdown-option', { active: this.activeFilters.type.includes('sectionEmpty') }],
                                onClick: () => {
                                    window.location.href = '/sections';
                                    this.mobileFilterDropdownOpen = false;
                                }
                            }, 'Sections'),
                            h('div', {
                                class: ['mobile-filter-dropdown-option', { active: this.activeFilters.type.includes('houseLand') }],
                                onClick: () => {
                                    window.location.href = '/house-land';
                                    this.mobileFilterDropdownOpen = false;
                                }
                            }, 'House & Land'),
                            h('div', {
                                class: ['mobile-filter-dropdown-option', { active: this.activeFilters.type.includes('showHome') }],
                                onClick: () => {
                                    window.location.href = '/show-homes';
                                    this.mobileFilterDropdownOpen = false;
                                }
                            }, 'Show homes')
                        ])
                    ])
                ])
            ]),*/

            // Type and View Filter Row (desktop) -- Hide filters for now since there's only sections
            /*h('div', { class: 'type-view-filter-row' }, [
                // Left side - Type filter
                h('div', { class: 'type-filter-section type-filter-section-desktop' }, [
                    h('label', { class: 'filter-label' }, 'Filter by:'),
                    h('div', { class: 'type-options' }, [
                        h('div', {
                            class: ['', { active: this.activeFilters.type.includes('sectionEmpty') }],
                            onClick: () => {
                                window.location.href = '/sections';
                            }
                        }, 'Sections'),
                        h('div', {
                            class: ['', { active: this.activeFilters.type.includes('houseLand') }],
                            onClick: () => {
                                window.location.href = '/house-land';
                            }
                        }, 'House & Land'),
                        h('div', {
                            class: ['', { active: this.activeFilters.type.includes('showHome') }],
                            onClick: () => {
                                window.location.href = '/show-homes';
                            }
                        }, 'Show homes')
                    ])
                ])
            ]),*/

            // Main Filters Row (only show in grid view regardless of type or screen size)
            (this.viewMode === 'grid') ? h('div', { class: ['main-filters-row', { 'main-filters-row--builder-catalog': ub }] }, [
                // Left side - All filters
                h('div', { class: 'filters-left' }, [
                    // Stage filter
                    this.visibleFilters.includes('stage') ? h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleDropdown('stage')
                        }, [
                            h('span', {}, this.getFilterLabel('stage')),
                            this.getActiveFilterCount('stage') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'stage' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('stage') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('stage'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearStageFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'stage' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-stage',
                                class: ['dropdown-option', { active: this.activeFilters.stage.length === 0 }],
                                onClick: () => this.clearStageFilter()
                            }, 'Any'),
                            ...this.stageOptions.map(stage => 
                                h('button', {
                                    key: stage.value,
                                    class: ['dropdown-option', { active: this.activeFilters.stage.includes(stage.value) }],
                                    onClick: () => this.toggleFilter('stage', stage.value)
                                }, stage.label)
                            )
                        ]) : null
                    ]) : null,

                    // Building providers filter (modifier when House & Land builder catalog only)
                    this.visibleFilters.includes('builder') ? h('div', { class: ['filter-dropdown', { 'filter-dropdown--builder-catalog': ub }] }, [
                        h('button', {
                            class: ['filter-button', { 'filter-button--builder-catalog': ub }],
                            onClick: () => this.toggleDropdown('builder')
                        }, [
                            h('span', {}, this.getFilterLabel('builder')),
                            this.getActiveFilterCount('builder') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'builder' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('builder') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('builder'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearBuilderFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'builder' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-builder',
                                class: ['dropdown-option', { active: this.activeFilters.builder.length === 0 }],
                                onClick: () => this.clearBuilderFilter()
                            }, 'Any'),
                            ...this.builderOptions.map(builder => 
                                h('button', {
                                    key: builder.value,
                                    class: ['dropdown-option', { active: this.activeFilters.builder.includes(builder.value) }],
                                    onClick: () => this.toggleFilter('builder', builder.value)
                                }, builder.label)
                            )
                        ]) : null
                    ]) : null,

                    // Bedrooms filter
                    this.visibleFilters.includes('bedrooms') ? h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleDropdown('bedrooms')
                        }, [
                            h('span', {}, this.getFilterLabel('bedrooms')),
                            this.getActiveFilterCount('bedrooms') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'bedrooms' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('bedrooms') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('bedrooms'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearBedroomsFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'bedrooms' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-bedrooms',
                                class: ['dropdown-option', { active: this.activeFilters.bedrooms.length === 0 }],
                                onClick: () => this.clearBedroomsFilter()
                            }, 'Any'),
                            ...this.bedroomsOptions.map(bedroom => 
                                h('button', {
                                    key: bedroom.value,
                                    class: ['dropdown-option', { active: this.activeFilters.bedrooms.includes(bedroom.value) }],
                                    onClick: () => this.toggleFilter('bedrooms', bedroom.value)
                                }, bedroom.label)
                            )
                        ]) : null
                    ]) : null,

                    // Cars filter
                    this.visibleFilters.includes('cars') ? h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleDropdown('cars')
                        }, [
                            h('span', {}, this.getFilterLabel('cars')),
                            this.getActiveFilterCount('cars') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'cars' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('cars') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('cars'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearCarsFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'cars' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-cars',
                                class: ['dropdown-option', { active: this.activeFilters.cars.length === 0 }],
                                onClick: () => this.clearCarsFilter()
                            }, 'Any'),
                            ...this.carsOptions.map(car => 
                                h('button', {
                                    key: car.value,
                                    class: ['dropdown-option', { active: this.activeFilters.cars.includes(car.value) }],
                                    onClick: () => this.toggleFilter('cars', car.value)
                                }, car.label)
                            )
                        ]) : null
                    ]) : null,

                    // Floor size filter
                    this.visibleFilters.includes('floorSize') ? h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleDropdown('floorSize')
                        }, [
                            h('span', {}, this.getFilterLabel('floorSize')),
                            this.getActiveFilterCount('floorSize') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'floorSize' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('floorSize') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('floorSize'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearFloorSizeFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'floorSize' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-floorSize',
                                class: ['dropdown-option', { active: this.activeFilters.floorSize.length === 0 }],
                                onClick: () => this.clearFloorSizeFilter()
                            }, 'Any'),
                            ...this.floorSizeOptions.map(size => 
                                h('button', {
                                    key: size.value,
                                    class: ['dropdown-option', { active: this.activeFilters.floorSize.includes(size.value) }],
                                    onClick: () => this.toggleFilter('floorSize', size.value)
                                }, size.label)
                            )
                        ]) : null
                    ]) : null,


                    // Section size filter
                    this.visibleFilters.includes('sectionSize') ? h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleDropdown('sectionSize')
                        }, [
                            h('span', {}, this.getFilterLabel('sectionSize')),
                            this.getActiveFilterCount('sectionSize') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'sectionSize' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('sectionSize') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('sectionSize'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearSectionSizeFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'sectionSize' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-sectionSize',
                                class: ['dropdown-option', { active: this.activeFilters.sectionSize.length === 0 }],
                                onClick: () => this.clearSectionSizeFilter()
                            }, 'Any'),
                            ...this.sectionSizeOptions.map(size => 
                                h('button', {
                                    key: size.value,
                                    class: ['dropdown-option', { active: this.activeFilters.sectionSize.includes(size.value) }],
                                    onClick: () => this.toggleFilter('sectionSize', size.value)
                                }, size.label)
                            )
                        ]) : null
                    ]) : null,



                    // Price filter
                    this.visibleFilters.includes('price') ? h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleDropdown('price')
                        }, [
                            h('span', {}, this.getFilterLabel('price')),
                            this.getActiveFilterCount('price') === 0 ? h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.openDropdown === 'price' }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]) : null,
                            this.getActiveFilterCount('price') > 0 ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value' }, [
                                    h('span', {}, this.getSelectedOptionLabel('price'))
                                ]),
                                h('span', { 
                                    class: 'filter-clear', 
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        this.clearPriceFilter();
                                    }
                                }, [
                                    h('svg', {
                                        width: '14',
                                        height: '14',
                                        viewBox: '0 0 24 24',
                                        fill: 'none',
                                        stroke: 'currentColor',
                                        'stroke-width': '2',
                                        'stroke-linecap': 'round',
                                        'stroke-linejoin': 'round'
                                    }, [
                                        h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                        h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                    ])
                                ])
                            ]) : null
                        ]),
                        this.openDropdown === 'price' ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'any-price',
                                class: ['dropdown-option', { active: this.activeFilters.price.length === 0 }],
                                onClick: () => this.clearPriceFilter()
                            }, 'Any'),
                            ...this.priceOptions.map(price => 
                                h('button', {
                                    key: price.value,
                                    class: ['dropdown-option', { active: this.activeFilters.price.includes(price.value) }],
                                    onClick: () => this.toggleFilter('price', price.value)
                                }, price.label)
                            )
                        ]) : null
                    ]) : null,


                    // All filters button (hidden on House & Land builder catalog — only one filter)
                    !ub ? h('button', {
                        class: 'all-filters-btn',
                        onClick: () => this.toggleMobileFilters()
                    }, [
                        h('svg', {
                            xmlns: 'http://www.w3.org/2000/svg',
                            width: '16',
                            height: '16',
                            viewBox: '0 0 24 24',
                            fill: 'currentColor'
                        }, [
                            h('path', { d: 'M17 8c.552 0 1 .449 1 1s-.448 1-1 1-1-.449-1-1 .448-1 1-1zm0-2c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3zm-10 6c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3zm10-8c.343 0 .677.035 1 .101v-2.101c0-.552-.447-1-1-1s-1 .448-1 1v2.101c.323-.066.657-.101 1-.101zm-10 6c.343 0 .677.035 1 .101v-8.101c0-.552-.447-1-1-1s-1 .448-1 1v8.101c.323-.066.657-.101 1-.101zm10 4c-.343 0-.677-.035-1-.101v8.101c0 .552.447 1 1 1s1-.448 1-1v-8.101c-.323.066-.657.101-1 .101zm-10 6c-.343 0-.677-.035-1-.101v2.101c0 .552.447 1 1 1s1-.448 1-1v-2.101c-.323.066-.657.101-1 .101z' })
                        ]),
                        h('span', 'All filters'),
                        this.getRelevantFilterCount() > 0 ? h('span', { class: 'filter-count-badge' }, this.getRelevantFilterCount()) : null
                    ]) : null,

                    // Reset link - only show if relevant filters are applied
                    this.getRelevantFilterCount() > 0 ? h('span', { 
                        class: 'reset-link',
                        onClick: (e) => {
                            e.stopPropagation();
                            this.resetFiltersExceptType();
                        },
                    }, 'Reset') : null,
                ]),

                // Right side - Sort dropdown (not on builder catalog)
                !ub ? h('div', { class: 'filters-right' }, [
                    // Sort dropdown
                    h('div', { class: 'filter-dropdown' }, [
                        h('button', {
                            class: 'filter-button',
                            onClick: () => this.toggleSortDropdown()
                        }, [
                            h('span', {}, 'Sort by'),
                            this.sortField ? h('div', { class: 'filter-count' }, [
                                h('div', { class: 'filter-value filter-value-sort' }, [
                                    h('span', {}, this.getSortFieldLabel())
                                ])
                            ]) : null,
                            h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '9.717',
                                height: '6',
                                viewBox: '0 0 9.717 6',
                                class: ['filter-chevron', { 'rotated': this.sortDropdownOpen }]
                            }, [
                                h('path', { 
                                    id: 'Path_30231', 
                                    'data-name': 'Path_30231', 
                                    d: 'M105.732-1176l-1.142,1.142,3.709,3.717-3.709,3.716,1.142,1.142,4.858-4.858Z', 
                                    transform: 'translate(-1166.283 -104.59) rotate(90)', 
                                    fill: '#2f412f', 
                                    'fill-rule': 'evenodd' 
                                })
                            ])
                        ]),
                        this.sortDropdownOpen ? h('div', { class: 'dropdown-content' }, [
                            h('button', {
                                key: 'sort-asc',
                                class: ['dropdown-option', { active: this.sortField === 'sort' }],
                                onClick: () => this.setSort('sort', 'asc')
                            }, 'Lot number'),
                            this.visibleFilters.includes('price') ? h('button', {
                                key: 'price-asc',
                                class: ['dropdown-option', { active: this.sortField === 'price' && this.sortDirection === 'asc' }],
                                onClick: () => this.setSort('price', 'asc')
                            }, 'Price (low to high)') : null,
                            this.visibleFilters.includes('price') ? h('button', {
                                key: 'price-desc',
                                class: ['dropdown-option', { active: this.sortField === 'price' && this.sortDirection === 'desc' }],
                                onClick: () => this.setSort('price', 'desc')
                            }, 'Price (high to low)') : null,
                            h('button', {
                                key: 'sectionSize-asc',
                                class: ['dropdown-option', { active: this.sortField === 'sectionSize' && this.sortDirection === 'asc' }],
                                onClick: () => this.setSort('sectionSize', 'asc')
                            }, 'Section size (small to large)'),
                            h('button', {
                                key: 'sectionSize-desc',
                                class: ['dropdown-option', { active: this.sortField === 'sectionSize' && this.sortDirection === 'desc' }],
                                onClick: () => this.setSort('sectionSize', 'desc')
                            }, 'Section size (large to small)'),
                            this.visibleFilters.includes('floorSize') ? h('button', {
                                key: 'floorSize-asc',
                                class: ['dropdown-option', { active: this.sortField === 'floorSize' && this.sortDirection === 'asc' }],
                                onClick: () => this.setSort('floorSize', 'asc')
                            }, 'Floor size (small to large)') : null,
                            this.visibleFilters.includes('floorSize') ? h('button', {
                                key: 'floorSize-desc',
                                class: ['dropdown-option', { active: this.sortField === 'floorSize' && this.sortDirection === 'desc' }],
                                onClick: () => this.setSort('floorSize', 'desc')
                            }, 'Floor size (large to small)') : null
                        ]) : null
                    ])
                ]) : null
            ]) : null,

            // Search and Sort Row
            // < 768px: hide in map; >= 768 and < 1024px: show only in grid; >= 1024px: show in both grid and map
            !ub && ((this.windowWidth >= 1024) || this.viewMode === 'grid') ? h('div', { class: 'search-sort-row' }, [
                // Left side - Search
                h('div', { class: 'search-sort-left' }, [
                    // Search by lot number
                    h('div', { class: 'search-container' }, [
                        h('div', { class: 'search-input-wrapper' }, [
                            h('svg', {
                                xmlns: 'http://www.w3.org/2000/svg',
                                width: '14.905',
                                height: '14.905',
                                viewBox: '0 0 14.905 14.905',
                                class: 'search-icon'
                            }, [
                                h('path', { 
                                    id: 'iconmonstr-search-thin', 
                                    d: 'M9.248,9.66a5.545,5.545,0,1,1,.412-.412L14,13.588,13.588,14,9.248,9.66ZM5.542.583A4.958,4.958,0,1,1,.583,5.542,4.961,4.961,0,0,1,5.542.583Z', 
                                    transform: 'translate(0.375 0.375)', 
                                    fill: '#080808', 
                                    stroke: '#080808', 
                                    'stroke-width': '0.75', 
                                    'fill-rule': 'evenodd' 
                                })
                            ]),
                            h('input', {
                                type: 'text',
                                class: 'search-input',
                                placeholder: this.searchPlaceholder,
                                value: this.searchTerm,
                                onKeydown: (e) => {
                                    if (e.key === 'Enter') e.preventDefault();
                                },
                                onInput: (e) => {
                                    this.searchTerm = e.target.value;
                                    this.currentPage = 1;
                                    this.updateDisplayedLots();
                                    this.updateURL();
                                    if (this.mobileFiltersOpen) this.updateMobileFilterActiveStates();
                                }
                            }),
                            this.searchTerm ? h('button', {
                                class: 'search-clear-btn',
                                onClick: (e) => {
                                    e.preventDefault();
                                    this.searchTerm = '';
                                    this.currentPage = 1;
                                    this.updateDisplayedLots();
                                    this.updateURL();
                                    if (this.mobileFiltersOpen) this.updateMobileFilterActiveStates();
                                },
                                type: 'button'
                            }, [
                                h('svg', {
                                    width: '14',
                                    height: '14',
                                    viewBox: '0 0 24 24',
                                    fill: 'none',
                                    stroke: 'currentColor',
                                    'stroke-width': '2',
                                    'stroke-linecap': 'round',
                                    'stroke-linejoin': 'round'
                                }, [
                                    h('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                                    h('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
                                ])
							]) : null,
							// Mobile (<768), Sections view, non-empty search, and no results: show helper text under input
							(this.windowWidth < 768 
								&& this.activeFilters.type.includes('sectionEmpty')
								&& this.searchTerm && this.searchTerm.trim() !== ''
								&& this.filteredLots.length === 0) 
									? h('div', { class: 'search-no-results' }, 'No lots found. Try adjusting your filters.') 
									: null
                        ])
                    ])
                ])
            ]) : null,

            // Results counter row for grid view (only show in grid view)
            this.viewMode === 'grid' ? h('div', { class: 'results-counter-row' }, [
                h('div', { class: 'results-counter' }, [
                    h('span', { class: 'results-number' }, `${ub ? this.filteredBuilders.length : this.filteredLots.length}`),
                    h('span', { class: 'results-text' }, (ub ? this.filteredBuilders.length : this.filteredLots.length) === 1 ? ' result' : ' results')
                ])
            ]) : null,

            // Results Section (only show in grid view)
            this.viewMode === 'grid' ? h('div', { class: 'lot-results' }, [
                (ub ? this.displayedBuilders.length === 0 : this.displayedLots.length === 0)
                    ? h('div', { class: 'no-results' }, [
                        h('div', { class: 'no-results-icon' }, [
                            h('img', {
                                src: '/dist/images/icons/logo-icon.svg',
                                alt: 'No results',
                            })
                        ]),
                        h('h3', ub ? 'No builders found' : 'No lots found'),
                        h('p', ub ? 'Try choosing a different builder.' : 'Try adjusting your filters to see more results.'),
                        h('button', {
                            class: 'btn',
                            onClick: this.resetFiltersExceptType
                        }, 'Clear All Filters')
                    ])
                    : h('div', { class: ['lots-grid', { 'lots-grid--builders': ub }] }, 
                        ub
                            ? this.displayedBuilders.map(b => this.renderBuilderListingCard(b))
                            : this.displayedLots.map(lot => 
                                h('div', {
                                    key: lot.id,
                                    class: 'lot-card-container',
                                    'data-lot-id': lot.id,
                                    innerHTML: `<div class="loading-card">Loading...</div>`
                                })
                            )
                    ),

                // Load More Button
                this.hasMoreLots ? h('div', { class: 'load-more-section' }, [
                    h('button', {
                        class: 'btn',
                        onClick: this.loadMore
                    }, [
                        `Load More (${this.remainingLotsCount} remaining)`,
                    ])
                ]) : null
            ]) : null,

        ]);
    },
    updated() {
        if (this.useBuilderCatalog) {
            return;
        }
        this.$nextTick(() => {
            this.displayedLots.forEach(lot => {
                const cardContainer = document.querySelector(`[data-lot-id="${lot.id}"]`);
                if (cardContainer && !cardContainer.dataset.loaded) {
                    this.loadCardHtml(lot.id, cardContainer);
                }
            });
        });
    }
}
</script>
